import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useRealtimeFrame } from '../../shared/api/realtime';
import {
  getAiBudgetToday,
  getAiUsage,
  updateOrganization,
  type TAiUsageWindow,
} from '../../entities/organization';

const KEY = ['ai-budget'] as const;
const USAGE = ['ai-usage'] as const;
const QUIET_MS = 3_000;

export const useAiBudget = () => {
  const client = useQueryClient();
  const today = useQuery({ queryKey: KEY, queryFn: getAiBudgetToday });

  // Spend moves when runs finish; the run frame is the signal to re-read it, not a timer.
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useRealtimeFrame('flow_run.updated', () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      void client.invalidateQueries({ queryKey: KEY });
      void client.invalidateQueries({ queryKey: USAGE });
    }, QUIET_MS);
  });
  useEffect(() => () => clearTimeout(timer.current), []);

  const save = useMutation({
    mutationFn: (usd: number | null) => updateOrganization({ aiDailyBudgetUsd: usd }),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: KEY }),
        client.invalidateQueries({ queryKey: ['organization'] }),
      ]),
  });

  return { today, save };
};

export const useAiUsage = (window: TAiUsageWindow) =>
  useQuery({ queryKey: [...USAGE, window], queryFn: () => getAiUsage(window) });

export type TAiBudget = ReturnType<typeof useAiBudget>;
